/**
 * 사주팔자 계산 오케스트레이터.
 *
 * 년주·월주·일주·시주 계산을 하나의 진입점으로 통합한다.
 * 음력→양력 변환 및 진태양시 보정을 적용한 후 각 기둥을 산출한다.
 */

import type { FourPillars } from '../types/pillars';
import type { TimeCorrectionInfo } from '../types/input';
import { getYearPillar } from './year-pillar';
import { getMonthPillar } from './month-pillar';
import { getDayPillar, getDayPillarRaw } from './day-pillar';
import { getHourPillar } from './hour-pillar';
import { applyTrueSolarTimeCorrection, addDays, validateSolarDate } from '../calendar/date-utils';
import { lunarToSolar } from '../calendar/lunar-calendar';

export type FourPillarsResult = FourPillars & { timeCorrection?: TimeCorrectionInfo };

export interface FourPillarsInput {
  /** 양력 또는 음력 연도 */
  year: number;
  /** 월 (1-12) */
  month: number;
  /** 일 */
  day: number;
  /** 시 (0-23) */
  hour: number;
  /** 분 (0-59) */
  minute: number;
  /** 성별 */
  gender: 'male' | 'female';
  /** 양력/음력 구분 (기본값: 'solar') */
  calendar?: 'solar' | 'lunar';
  /** 음력 윤달 여부 (calendar가 'lunar'일 때만 사용) */
  isLeapMonth?: boolean;
  /** 출생지 경도 (기본값: 126.978, 서울) */
  longitude?: number;
  /** 진태양시 보정 적용 여부 (기본값: true) */
  useTrueSolarTime?: boolean;
  /** 진태양시 대신 고정 -30분 보정 사용 (default: false). true이면 useTrueSolarTime 무시. */
  useFixedTimeOffset?: boolean;
  /** 야자시 적용 여부 (default: true). false이면 조자시(23시에도 일주 변경 안 함) */
  useYajasi?: boolean;
}

/**
 * 생년월일시 정보로 사주 네 기둥을 계산한다.
 *
 * 처리 순서:
 * 1. 음력이면 양력으로 변환
 * 2. 양력 날짜 유효성 검증
 * 3. 시간 보정 적용 (진태양시 또는 고정 -30분)
 * 4. 년주 계산 (입춘 기준)
 * 5. 월주 계산 (절기 + 오호둔월법)
 * 6. 일주 계산 (율리우스일 + 야자시 보정)
 * 7. 시주 계산 (오자둔시법)
 *
 * @param input - 생년월일시 입력 정보
 * @returns 사주 네 기둥
 */
export function calculateFourPillars(input: FourPillarsInput): FourPillarsResult {
  let { year, month, day, hour, minute } = input;
  const longitude = input.longitude ?? 126.978;
  const useTrueSolarTime = input.useTrueSolarTime ?? true;

  // Step 1: Convert lunar to solar if needed
  if (input.calendar === 'lunar') {
    const solar = lunarToSolar(year, month, day, input.isLeapMonth ?? false);
    year = solar.year;
    month = solar.month;
    day = solar.day;
  }

  // Step 2: Validate the solar date
  validateSolarDate(year, month, day);

  // Step 3: Apply time correction
  const useFixedTimeOffset = input.useFixedTimeOffset ?? false;
  const pad = (n: number) => String(n).padStart(2, '0');
  let timeCorrection: TimeCorrectionInfo | undefined;

  if (useFixedTimeOffset) {
    // 고정 -30분 보정
    const FIXED_OFFSET = -30;
    let totalMinutes = hour * 60 + minute + FIXED_OFFSET;
    let dayOffset = 0;
    if (totalMinutes < 0) {
      totalMinutes += 1440;
      dayOffset = -1;
    }
    hour = Math.floor(totalMinutes / 60);
    minute = totalMinutes % 60;

    timeCorrection = {
      applied: true,
      method: 'fixedOffset',
      originalTime: `${pad(input.hour)}:${pad(input.minute)}`,
      correctedTime: `${pad(hour)}:${pad(minute)}`,
      longitudeCorrection: FIXED_OFFSET,
      equationOfTime: 0,
      totalCorrection: FIXED_OFFSET,
      dayOffset,
    };
  } else if (useTrueSolarTime) {
    const corrected = applyTrueSolarTimeCorrection(year, month, day, hour, minute, longitude);
    const totalCorrection = corrected.longitudeCorrection + corrected.equationOfTime;

    hour = corrected.hour;
    minute = corrected.minute;

    timeCorrection = {
      applied: true,
      method: 'trueSolarTime',
      originalTime: `${pad(input.hour)}:${pad(input.minute)}`,
      correctedTime: `${pad(corrected.hour)}:${pad(corrected.minute)}`,
      longitudeCorrection: Math.round(corrected.longitudeCorrection * 100) / 100,
      equationOfTime: Math.round(corrected.equationOfTime * 100) / 100,
      totalCorrection: Math.round(totalCorrection * 100) / 100,
      dayOffset: corrected.dayOffset,
    };
  }

  // 날짜 경계 조정 (공통)
  if (timeCorrection && timeCorrection.dayOffset !== 0) {
    const adjusted = addDays(year, month, day, timeCorrection.dayOffset);
    year = adjusted.year;
    month = adjusted.month;
    day = adjusted.day;
  }

  // Step 4: Year pillar
  const yearPillar = getYearPillar(year, month, day, hour, minute);

  // Step 5: Month pillar (uses year stem for 오호둔월법)
  const monthPillar = getMonthPillar(yearPillar.stem, year, month, day, hour, minute);

  // Step 6: Day pillar (handles 야자시 internally)
  const useYajasi = input.useYajasi ?? true;
  const dayPillar = useYajasi
    ? getDayPillar(year, month, day, hour)
    : getDayPillarRaw(year, month, day);

  // Step 7: Hour pillar (uses day stem for 오자둔시법)
  // Note: getDayPillar already adjusts the day stem for 야자시
  const hourPillar = getHourPillar(dayPillar.stem, hour);

  return {
    year: yearPillar,
    month: monthPillar,
    day: dayPillar,
    hour: hourPillar,
    timeCorrection,
  };
}
